import { ProductsEtaContextProvider } from '@app/components/products/ProductsEta';
import { Button } from '@app/components/various/Button';
import { FocusTrap } from '@app/components/various/FocusTrap';
import { CancelSmall } from '@app/components/various/Icons';
import { Modal } from '@app/components/various/Modal';
import { Portal } from '@app/components/various/Portal';
import { getActiveDeliveryWindowsIdsForOrderBuyer, getIsStockTypeSeparationEnabled, getOrderDetails } from '@app/ducks';
import { clearProductSearchQuery, getUrlForProductAndVariant } from '@app/logic/products';
import { paths } from '@app/paths';
import { getProductQuery, useProduct } from '@app/services/hooks/products/useProduct';
import { GAProductListItemId, GAProductListItemName, mapProductsToGAItems, useGTM } from '@app/utils/analytics';
import { useDelwinIdsWithCurrentStockType } from '@app/utils/hooks';
import { useBodyLock } from '@app/utils/hooks/useBodyLock';
import { useSearchParamByName } from '@app/utils/hooks/useSearchParamByName';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useMatch, useNavigate } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import { ProductDetailsLayoutContextProvider } from '../context/ProductDetailsLayoutContext';
import { useProductDetailsModalContext } from '../context/ProductDetailsModalContext';
import { ProductDetailsVariantContextProvider } from '../context/ProductDetailsVariantContext';

import { NavigationArrows } from './NavigationArrows';
import { ProductDetailsModalContent } from './ProductDetailsModalContent';
import { ProductDetailsModalLoader } from './ProductDetailsModalLoader';
import { ProductNotFoundContent } from './ProductNotFoundContent';

export const ProductDetailsModal = () => {
  const { productId, variantId, buyerId, previousProductId, nextProductId } = useProductDetailsModalContext();
  const scannedBarcode = useSearchParamByName('barcode');
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const orderDetails = useSelector(getOrderDetails);
  const { data, isError: hasProductDetailsFailed } = useProduct(productId, [], buyerId);
  const productDetails = data?.product;
  const isStockTypeSeparationEnabled = useSelector(getIsStockTypeSeparationEnabled);
  const activeDeliveryWindowsIds = useSelector(getActiveDeliveryWindowsIdsForOrderBuyer);
  const delwinsWithCurrentStockType = useDelwinIdsWithCurrentStockType(activeDeliveryWindowsIds);
  const currency = orderDetails?.currency.currency;
  const orderNumber = orderDetails.order.order;

  const isAllProductsPage = useMatch(paths.PRODUCTS_ORDER)?.pattern.end ?? false;
  const hasProductId = isDefined(productId) && !isEmpty(productId);
  const { trackGTMEvent } = useGTM();

  useBodyLock(hasProductId);

  const hideModal = React.useCallback(() => {
    navigate(clearProductSearchQuery(location.search));
  }, [navigate, location.search]);

  React.useEffect(() => {
    if (!isDefined(productId) || !isDefined(buyerId)) return;

    [previousProductId, nextProductId].forEach(id => {
      if (!isDefined(id)) return;
      queryClient.prefetchQuery(getProductQuery(id, [], buyerId));
    });
  }, [productId, buyerId, queryClient, previousProductId, nextProductId]);

  React.useEffect(() => {
    if (!isDefined(productDetails)) {
      return;
    }
    trackGTMEvent('view_item', {
      ecommerce: {
        currency,
        items: mapProductsToGAItems([productDetails], {
          item_list_id: GAProductListItemId.Selection,
          item_list_name: GAProductListItemName.Selection,
        }),
        value: productDetails.priceAsNumber,
      },
    });
  }, [productDetails, trackGTMEvent, orderNumber, currency]);

  return (
    <>
      <Portal>
        <CSSTransition
          in={hasProductId && !hasProductDetailsFailed}
          timeout={150}
          classNames={{
            enter: 'opacity-0 scale-90',
            enterActive: '!opacity-100 !scale-100 transition-[opacity,transform]',
            exit: 'opacity-100 scale-100',
            exitActive: '!opacity-0 !scale-90 transition-[opacity,transform]',
          }}
          unmountOnExit
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div className="fixed inset-0 z-pdm h-full w-full bg-neutral-96">
            <FocusTrap>
              <ProductDetailsLayoutContextProvider>
                {!isDefined(productDetails) ?
                  <ProductDetailsModalLoader onHideModal={hideModal} />
                : <ProductDetailsVariantContextProvider
                    productId={productId}
                    variantId={variantId}
                    productDetails={productDetails}
                    getProductUrl={getUrlForProductAndVariant}
                    delwinIds={isStockTypeSeparationEnabled ? delwinsWithCurrentStockType : undefined}
                >
                    <ProductsEtaContextProvider products={[productDetails]} isAllProductsPage={isAllProductsPage}>
                      <ProductDetailsModalContent onHideModal={hideModal} scannedBarcode={scannedBarcode} />
                    </ProductsEtaContextProvider>
                  </ProductDetailsVariantContextProvider>
                }
                <div className="absolute left-0 right-0 top-0 flex justify-between gap-5 p-4 sm:hidden">
                  <NavigationArrows />
                  <Button
                    icon={CancelSmall}
                    aria-label="Close modal"
                    color="secondary"
                    variant="flat"
                    className="z-pdm"
                    onClick={hideModal}
                  />
                </div>
              </ProductDetailsLayoutContextProvider>
            </FocusTrap>
          </div>
        </CSSTransition>
      </Portal>
      <Modal isOpen={hasProductId && hasProductDetailsFailed} onClose={hideModal}>
        <ProductNotFoundContent />
      </Modal>
    </>
  );
};
